/**
 * Dashboard page — role-aware per COMP-007 Story 1 (Admin) and Story 9 (Technician).
 * Admin: 4 stat cards, pending actions, recent activity, system health, customer activity.
 * Technician: 3 stat cards, own recent sessions, own activity feed.
 */

import { useMemo } from "react";
import {
  Users,
  ShieldCheck,
  UserCheck,
  ClipboardCheck,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
  AlertTriangle,
  BarChart3,
  type LucideIcon,
} from "lucide-react";
import { GlassCard } from "@/components/sc/glass-card";
import { StatusBadge } from "@/components/sc/status-badge";
import { CountdownTimer } from "@/components/sc/countdown-timer";
import { useRole } from "@/hooks/use-role";
import { useDashboardStats, useDashboardActivity } from "@/hooks/use-dashboard";
import { useHealth } from "@/hooks/use-health";
import { usePimSessionsMine } from "@/hooks/use-pim-sessions";
import { usePendingApprovals } from "@/hooks/use-approvals";
import { useGdapRelationships } from "@/hooks/use-gdap";
import type { AuditLogEntry, HealthStatus, PIMSession } from "@/types";

// ---- Relative time formatting ----

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ---- Stat Card ----

interface StatCardProps {
  readonly label: string;
  readonly value: number;
  readonly icon: LucideIcon;
  readonly trend?: number[];
}

function StatCard({ label, value, icon: Icon, trend }: StatCardProps) {
  // Calculate trend direction from last 7 data points
  let trendPct: number | null = null;
  let trendUp = false;
  if (trend && trend.length >= 2) {
    const first = trend[0]!;
    const last = trend[trend.length - 1]!;
    if (first > 0) {
      trendPct = Math.round(((last - first) / first) * 100);
      trendUp = trendPct >= 0;
    }
  }

  return (
    <GlassCard className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-label text-sc-text-muted uppercase tracking-widest">
          {label}
        </span>
        <Icon size={18} className="text-sc-text-muted" />
      </div>
      <div className="flex items-end gap-2">
        <span className="text-stat font-display font-extrabold tracking-tighter text-sc-navy">
          {value}
        </span>
        {trendPct !== null && (
          <span
            className={`flex items-center gap-0.5 text-label font-medium ${
              trendUp ? "text-sc-success" : "text-sc-danger"
            }`}
          >
            {trendUp ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
            {Math.abs(trendPct)}%
          </span>
        )}
      </div>
      {/* Mini sparkline */}
      {trend && trend.length > 1 && (
        <Sparkline data={trend} className="mt-1" />
      )}
    </GlassCard>
  );
}

// ---- Mini Sparkline (SVG) ----

function Sparkline({
  data,
  className,
}: {
  readonly data: number[];
  readonly className?: string;
}) {
  const width = 120;
  const height = 28;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;

  const points = data
    .map((v, i) => {
      const x = (i / (data.length - 1)) * width;
      const y = height - ((v - min) / range) * height;
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={className}
      aria-hidden="true"
    >
      <polyline
        points={points}
        fill="none"
        stroke="var(--sc-green)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ---- Loading Spinner (DR-003) ----

function LoadingSpinner({ resource }: { readonly resource: string }) {
  return (
    <GlassCard className="flex items-center justify-center py-16">
      <div className="text-center">
        <div className="mx-auto mb-2 h-6 w-6 rounded-full bg-sc-primary animate-sc-pulse" />
        <p className="text-body-sm text-sc-text-muted">Loading {resource}...</p>
      </div>
    </GlassCard>
  );
}

// ---- Error Card (Story 1 error path) ----

function ErrorCard({ message }: { readonly message: string }) {
  return (
    <GlassCard className="flex items-center justify-center py-16">
      <div className="text-center">
        <p className="text-body text-sc-text font-medium mb-1">{message}</p>
        <p className="text-body-sm text-sc-text-muted">Retrying...</p>
      </div>
    </GlassCard>
  );
}

// ---- System Health Panel (Story 1 Step 3) ----

const SERVICE_LABELS: Record<string, string> = {
  halopsa: "HaloPSA API",
  graph: "Microsoft Graph API",
  redis: "Redis Cache",
  sql: "Azure SQL",
  signalr: "SignalR Hub",
};

function HealthPanel({ health }: { readonly health: HealthStatus }) {
  const services = Object.entries(health.services);

  return (
    <GlassCard>
      <h3 className="text-h3 font-display text-sc-navy mb-3">System Health</h3>
      <div className="space-y-2">
        {services.map(([key, status]) => (
          <div key={key} className="flex items-center justify-between">
            <span className="text-body-sm text-sc-text">
              {SERVICE_LABELS[key] ?? key}
            </span>
            <div className="flex items-center gap-2">
              <span
                className={`h-2.5 w-2.5 rounded-full ${
                  status === "connected"
                    ? "bg-sc-success animate-sc-pulse"
                    : "bg-sc-danger"
                }`}
                aria-label={`${SERVICE_LABELS[key] ?? key}: ${status === "connected" ? "Operational" : "Down"}`}
              />
              <span className="text-label text-sc-text-muted">
                {status === "connected" ? "Operational" : "Down"}
              </span>
            </div>
          </div>
        ))}
      </div>
      {health.status === "degraded" && (
        <p className="mt-2 text-label text-amber-600 dark:text-amber-400">
          System degraded — some services may be unavailable
        </p>
      )}
    </GlassCard>
  );
}

function HealthPanelUnknown() {
  return (
    <GlassCard>
      <h3 className="text-h3 font-display text-sc-navy mb-3">System Health</h3>
      <div className="space-y-2">
        {Object.entries(SERVICE_LABELS).map(([key, label]) => (
          <div key={key} className="flex items-center justify-between">
            <span className="text-body-sm text-sc-text">{label}</span>
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-gray-400" />
              <span className="text-label text-sc-text-muted">Unknown</span>
            </div>
          </div>
        ))}
      </div>
    </GlassCard>
  );
}

// ---- User action event types for "Recent Activity" ----

const USER_ACTION_EVENTS = [
  "GDAP_RELATIONSHIP_CREATED",
  "GDAP_RELATIONSHIP_TERMINATED",
  "GDAP_ASSIGNMENTS_CREATED",
  "PIM_SESSION_ACTIVATED",
  "PIM_SESSION_EXTENDED",
  "PIM_SESSION_DEACTIVATED",
  "PIM_SESSION_REVOKED",
  "PIM_APPROVAL_GRANTED",
  "PIM_APPROVAL_DENIED",
  "PIM_APPROVAL_REQUESTED",
  "TECHNICIAN_ATTRIBUTES_UPDATED",
  "TECHNICIAN_ACCESS_REVOKED",
  "TECHNICIAN_TIER_CHANGED",
  "CUSTOMER_CONFIG_CHANGED",
  "CUSTOMER_ACCESS_TYPE_CHANGED",
  "CACHE_REFRESH_MANUAL",
];

// ---- Activity Feed (Story 1 Step 4) ----

const SEVERITY_DOT: Record<string, string> = {
  INFO: "bg-blue-500",
  WARNING: "bg-amber-500",
  CRITICAL: "bg-sc-danger animate-sc-pulse",
};

function ActivityFeed({ events }: { readonly events: AuditLogEntry[] }) {
  if (events.length === 0) {
    return (
      <GlassCard>
        <h3 className="text-h3 font-display text-sc-navy mb-3">
          Recent Activity
        </h3>
        <p className="text-body-sm text-sc-text-muted">
          No recent activity to display.
        </p>
      </GlassCard>
    );
  }

  return (
    <GlassCard>
      <h3 className="text-h3 font-display text-sc-navy mb-3">
        Recent Activity
      </h3>
      <div className="space-y-3">
        {events.map((event) => (
          <div
            key={event.id}
            className="flex items-start gap-3 text-body-sm"
          >
            <span
              className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${SEVERITY_DOT[event.severity] ?? SEVERITY_DOT.INFO}`}
              aria-label={`Severity: ${event.severity}`}
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sc-text truncate">
                  {event.actorDisplayName ?? "System"}
                </span>
                <span className="text-label text-sc-text-muted whitespace-nowrap">
                  {relativeTime(event.timestamp)}
                </span>
              </div>
              <p className="text-sc-text-muted truncate">{event.description}</p>
            </div>
          </div>
        ))}
      </div>
    </GlassCard>
  );
}

// ---- Technician Sessions Table (Story 9 Step 2) ----

function TechnicianSessionsTable({
  sessions,
}: {
  readonly sessions: PIMSession[];
}) {
  if (sessions.length === 0) {
    return (
      <GlassCard>
        <h3 className="text-h3 font-display text-sc-navy mb-3">
          Recent Sessions
        </h3>
        <p className="text-body-sm text-sc-text-muted">
          No active sessions. Access a customer from the Customers page or via
          your HaloPSA ticket link.
        </p>
      </GlassCard>
    );
  }

  return (
    <GlassCard noPadding>
      <div className="px-6 pt-4 pb-2">
        <h3 className="text-h3 font-display text-sc-navy">Recent Sessions</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full" role="table">
          <thead>
            <tr className="border-b border-sc-border" role="row">
              <th
                role="columnheader"
                className="px-6 py-3 text-left text-table-header text-sc-text-muted uppercase tracking-wider"
              >
                Customer
              </th>
              <th
                role="columnheader"
                className="px-6 py-3 text-left text-table-header text-sc-text-muted uppercase tracking-wider"
              >
                Status
              </th>
              <th
                role="columnheader"
                className="px-6 py-3 text-left text-table-header text-sc-text-muted uppercase tracking-wider"
              >
                Roles
              </th>
              <th
                role="columnheader"
                className="px-6 py-3 text-left text-table-header text-sc-text-muted uppercase tracking-wider"
              >
                Activated
              </th>
              <th
                role="columnheader"
                className="px-6 py-3 text-left text-table-header text-sc-text-muted uppercase tracking-wider"
              >
                Expires
              </th>
            </tr>
          </thead>
          <tbody>
            {sessions.map((session) => (
              <tr
                key={session.id}
                role="row"
                className="border-b border-sc-border/50 last:border-b-0"
              >
                <td role="cell" className="px-6 py-3 text-body-sm text-sc-text">
                  {session.customerName}
                </td>
                <td role="cell" className="px-6 py-3">
                  <StatusBadge status={session.status} category="pim" />
                </td>
                <td role="cell" className="px-6 py-3 text-body-sm text-sc-text">
                  {session.roles.length}
                </td>
                <td
                  role="cell"
                  className="px-6 py-3 text-body-sm text-sc-text-muted"
                >
                  {relativeTime(session.activatedAt)}
                </td>
                <td role="cell" className="px-6 py-3">
                  {session.status === "Active" ? (
                    <CountdownTimer expiresAt={session.expiresAt} />
                  ) : (
                    <span className="text-body-sm text-sc-text-muted">
                      {relativeTime(session.expiresAt)}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </GlassCard>
  );
}

// ---- Main Dashboard ----

export function DashboardPage() {
  const role = useRole();
  const isAdmin = role === "admin";

  return isAdmin ? <AdminDashboard /> : <TechnicianDashboard />;
}

// ---- Admin Dashboard (Story 1) ----

function AdminDashboard() {
  const statsQuery = useDashboardStats();
  const activityQuery = useDashboardActivity(10, USER_ACTION_EVENTS);
  const healthQuery = useHealth();
  const pendingApprovalsQuery = usePendingApprovals();
  const gdapQuery = useGdapRelationships({ page: 1, pageSize: 500 });

  const stats = statsQuery.data;
  const activity = activityQuery.data?.items ?? [];
  const pendingApprovals = pendingApprovalsQuery.data?.items ?? [];

  // Compute customer activity summary from user action events (last 50)
  const allActivityQuery = useDashboardActivity(50, USER_ACTION_EVENTS);
  const customerActivity = useMemo(() => {
    const items = allActivityQuery.data?.items ?? [];
    const map = new Map<string, { name: string; total: number; actions: Map<string, number> }>();
    for (const event of items) {
      if (!event.tenantName || event.tenantName.toUpperCase() === "SYSTEM") continue;
      let entry = map.get(event.tenantId);
      if (!entry) {
        entry = { name: event.tenantName, total: 0, actions: new Map() };
        map.set(event.tenantId, entry);
      }
      entry.total++;
      entry.actions.set(event.eventType, (entry.actions.get(event.eventType) ?? 0) + 1);
    }
    return [...map.values()]
      .sort((a, b) => b.total - a.total)
      .slice(0, 10)
      .map((c) => ({
        name: c.name,
        total: c.total,
        actions: [...c.actions.entries()]
          .sort(([, a], [, b]) => b - a)
          .map(([type, count]) => ({ type, label: formatEventType(type), count })),
      }));
  }, [allActivityQuery.data?.items]);

  // Compute technician activity summary from user action events (last 50)
  const technicianActivity = useMemo(() => {
    const items = allActivityQuery.data?.items ?? [];
    const map = new Map<string, { name: string; total: number; actions: Map<string, number> }>();
    for (const event of items) {
      if (!event.actorUserId || !event.actorDisplayName) continue;
      if (event.actorDisplayName.toUpperCase() === "SYSTEM") continue;
      let entry = map.get(event.actorUserId);
      if (!entry) {
        entry = { name: event.actorDisplayName, total: 0, actions: new Map() };
        map.set(event.actorUserId, entry);
      }
      entry.total++;
      entry.actions.set(event.eventType, (entry.actions.get(event.eventType) ?? 0) + 1);
    }
    return [...map.values()]
      .sort((a, b) => b.total - a.total)
      .slice(0, 10)
      .map((t) => ({
        name: t.name,
        total: t.total,
        actions: [...t.actions.entries()]
          .sort(([, a], [, b]) => b - a)
          .map(([type, count]) => ({ type, label: formatEventType(type), count })),
      }));
  }, [allActivityQuery.data?.items]);

  // Expiring GDAP relationships (within 30 days)
  const expiringGdap = useMemo(() => {
    const items = gdapQuery.data?.items ?? [];
    const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;
    return items
      .filter((r) => {
        if (r.status !== "Active" || !r.endDateTime) return false;
        const daysLeft = new Date(r.endDateTime).getTime() - Date.now();
        return daysLeft > 0 && daysLeft <= thirtyDaysMs;
      })
      .sort(
        (a, b) =>
          new Date(a.endDateTime!).getTime() -
          new Date(b.endDateTime!).getTime(),
      )
      .slice(0, 5);
  }, [gdapQuery.data?.items]);

  if (statsQuery.isError) {
    return <ErrorCard message="Unable to load dashboard data." />;
  }

  return (
    <div className="space-y-6">
      {/* Stat cards */}
      {statsQuery.isLoading ? (
        <LoadingSpinner resource="dashboard" />
      ) : stats ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <StatCard
            label="Total Customers"
            value={stats.totalCustomers}
            icon={Users}
            trend={stats.trends?.customers7d}
          />
          <StatCard
            label="Active Sessions"
            value={stats.activeSessions}
            icon={ShieldCheck}
            trend={stats.trends?.sessions7d}
          />
          <StatCard
            label="Active Technicians"
            value={stats.activeTechnicians}
            icon={UserCheck}
          />
          <StatCard
            label="Pending Reviews"
            value={stats.pendingReviews}
            icon={ClipboardCheck}
          />
        </div>
      ) : null}

      {/* Main content: two-column layout */}
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_370px] gap-6">
        {/* Left column — Pending Actions + Customer Activity */}
        <div className="space-y-6">
          <PendingActionsPanel
            pendingApprovals={pendingApprovals}
            expiringGdap={expiringGdap}
            isLoading={pendingApprovalsQuery.isLoading || gdapQuery.isLoading}
          />
          <CustomerActivityPanel
            customerActivity={customerActivity}
            isLoading={allActivityQuery.isLoading}
          />
          <TechnicianActivityPanel
            technicianActivity={technicianActivity}
            isLoading={allActivityQuery.isLoading}
          />
        </div>

        {/* Right column — System Health + Recent Activity */}
        <div className="space-y-6">
          {healthQuery.isLoading ? (
            <LoadingSpinner resource="health" />
          ) : healthQuery.isError ? (
            <HealthPanelUnknown />
          ) : healthQuery.data ? (
            <HealthPanel health={healthQuery.data} />
          ) : null}

          <RecentActivityList events={activity} isLoading={activityQuery.isLoading} />
        </div>
      </div>
    </div>
  );
}

// ---- Pending Actions Panel (Option A) ----

function PendingActionsPanel({
  pendingApprovals,
  expiringGdap,
  isLoading,
}: {
  readonly pendingApprovals: { id: string; requestedByName: string; customerName: string; activationTier: string; createdAt: string }[];
  readonly expiringGdap: { id: string; customerTenantName: string; endDateTime: string | null }[];
  readonly isLoading: boolean;
}) {
  if (isLoading) {
    return <LoadingSpinner resource="pending actions" />;
  }

  const hasItems = pendingApprovals.length > 0 || expiringGdap.length > 0;

  return (
    <GlassCard>
      <div className="flex items-center gap-2 mb-4">
        <AlertTriangle size={18} className="text-amber-500" />
        <h3 className="text-h3 font-display text-sc-navy">Attention Required</h3>
        {hasItems && (
          <span className="ml-auto text-label font-medium text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 rounded-full">
            {pendingApprovals.length + expiringGdap.length}
          </span>
        )}
      </div>

      {!hasItems ? (
        <p className="text-body-sm text-sc-text-muted">
          No pending actions. All clear.
        </p>
      ) : (
        <div className="space-y-3">
          {pendingApprovals.map((req) => (
            <div key={req.id} className="flex items-start gap-3 text-body-sm">
              <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-amber-500" />
              <div className="min-w-0 flex-1">
                <span className="font-medium text-sc-text">PIM Approval Pending</span>
                <p className="text-sc-text-muted truncate">
                  {req.requestedByName} — {req.customerName} ({req.activationTier})
                </p>
              </div>
              <span className="text-label text-sc-text-muted whitespace-nowrap">
                {relativeTime(req.createdAt)}
              </span>
            </div>
          ))}

          {expiringGdap.map((rel) => {
            const daysLeft = rel.endDateTime
              ? Math.ceil((new Date(rel.endDateTime).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
              : 0;
            return (
              <div key={rel.id} className="flex items-start gap-3 text-body-sm">
                <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${daysLeft <= 7 ? "bg-sc-danger animate-sc-pulse" : "bg-amber-500"}`} />
                <div className="min-w-0 flex-1">
                  <span className="font-medium text-sc-text">GDAP Expiring</span>
                  <p className="text-sc-text-muted truncate">
                    {rel.customerTenantName} — {daysLeft} day{daysLeft !== 1 ? "s" : ""} remaining
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </GlassCard>
  );
}

// ---- Customer Activity Panel (Option C) — Horizontal stacked bar chart ----

interface CustomerActivityItem {
  name: string;
  total: number;
  actions: { type: string; label: string; count: number }[];
}

const ACTION_COLORS: Record<string, string> = {
  PIM_SESSION_ACTIVATED: "#3cb34a",
  PIM_SESSION_DEACTIVATED: "#6366f1",
  PIM_SESSION_REVOKED: "#ef4444",
  PIM_SESSION_EXTENDED: "#14b8a6",
  PIM_APPROVAL_GRANTED: "#22c55e",
  PIM_APPROVAL_DENIED: "#f97316",
  PIM_APPROVAL_REQUESTED: "#8b5cf6",
  GDAP_RELATIONSHIP_CREATED: "#3b82f6",
  GDAP_RELATIONSHIP_TERMINATED: "#ec4899",
  GDAP_ASSIGNMENTS_CREATED: "#06b6d4",
  TECHNICIAN_ATTRIBUTES_UPDATED: "#a855f7",
  TECHNICIAN_ACCESS_REVOKED: "#f43f5e",
  TECHNICIAN_TIER_CHANGED: "#d946ef",
  CUSTOMER_CONFIG_CHANGED: "#f59e0b",
  CUSTOMER_ACCESS_TYPE_CHANGED: "#eab308",
  CACHE_REFRESH_MANUAL: "#94a3b8",
};

function getActionColor(type: string): string {
  return ACTION_COLORS[type] ?? "#94a3b8";
}

function CustomerActivityPanel({
  customerActivity,
  isLoading,
}: {
  readonly customerActivity: CustomerActivityItem[];
  readonly isLoading: boolean;
}) {
  if (isLoading) {
    return <LoadingSpinner resource="customer activity" />;
  }

  const maxTotal = Math.max(...customerActivity.map((c) => c.total), 1);

  // Collect unique action types across all customers for the legend
  const legendActions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const c of customerActivity) {
      for (const a of c.actions) {
        if (!seen.has(a.type)) seen.set(a.type, a.label);
      }
    }
    return [...seen.entries()].map(([type, label]) => ({ type, label }));
  }, [customerActivity]);

  return (
    <GlassCard>
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 size={18} className="text-sc-text-muted" />
        <h3 className="text-h3 font-display text-sc-navy">Customer Activity</h3>
      </div>
      {customerActivity.length === 0 ? (
        <p className="text-body-sm text-sc-text-muted">
          No recent customer activity.
        </p>
      ) : (
        <>
          {/* Bar chart */}
          <div className="space-y-3">
            {customerActivity.map((c) => (
              <div key={c.name} className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-body-sm font-medium text-sc-text truncate mr-2">
                    {c.name}
                  </span>
                  <span className="text-label text-sc-text-muted shrink-0">
                    {c.total}
                  </span>
                </div>
                <div
                  className="flex h-5 rounded-md overflow-hidden bg-sc-border/30"
                  style={{ width: `${(c.total / maxTotal) * 100}%`, minWidth: "24px" }}
                >
                  {c.actions.map((a) => (
                    <div
                      key={a.type}
                      className="h-full transition-all duration-500 first:rounded-l-md last:rounded-r-md"
                      style={{
                        width: `${(a.count / c.total) * 100}%`,
                        backgroundColor: getActionColor(a.type),
                      }}
                      title={`${a.label}: ${a.count}`}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-4 pt-3 border-t border-sc-border">
            {legendActions.map((a) => (
              <div key={a.type} className="flex items-center gap-1.5">
                <span
                  className="w-2.5 h-2.5 rounded-sm shrink-0"
                  style={{ backgroundColor: getActionColor(a.type) }}
                />
                <span className="text-label text-sc-text-muted">{a.label}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </GlassCard>
  );
}

// ---- Technician Activity Panel — Horizontal stacked bar chart (admin only) ----

function TechnicianActivityPanel({
  technicianActivity,
  isLoading,
}: {
  readonly technicianActivity: CustomerActivityItem[];
  readonly isLoading: boolean;
}) {
  if (isLoading) {
    return <LoadingSpinner resource="technician activity" />;
  }

  const maxTotal = Math.max(...technicianActivity.map((t) => t.total), 1);

  const legendActions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const t of technicianActivity) {
      for (const a of t.actions) {
        if (!seen.has(a.type)) seen.set(a.type, a.label);
      }
    }
    return [...seen.entries()].map(([type, label]) => ({ type, label }));
  }, [technicianActivity]);

  return (
    <GlassCard>
      <div className="flex items-center gap-2 mb-4">
        <UserCheck size={18} className="text-sc-text-muted" />
        <h3 className="text-h3 font-display text-sc-navy">Technician Activity</h3>
      </div>
      {technicianActivity.length === 0 ? (
        <p className="text-body-sm text-sc-text-muted">
          No recent technician activity.
        </p>
      ) : (
        <>
          <div className="space-y-3">
            {technicianActivity.map((t) => (
              <div key={t.name} className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-body-sm font-medium text-sc-text truncate mr-2">
                    {t.name}
                  </span>
                  <span className="text-label text-sc-text-muted shrink-0">
                    {t.total}
                  </span>
                </div>
                <div
                  className="flex h-5 rounded-md overflow-hidden bg-sc-border/30"
                  style={{ width: `${(t.total / maxTotal) * 100}%`, minWidth: "24px" }}
                >
                  {t.actions.map((a) => (
                    <div
                      key={a.type}
                      className="h-full transition-all duration-500 first:rounded-l-md last:rounded-r-md"
                      style={{
                        width: `${(a.count / t.total) * 100}%`,
                        backgroundColor: getActionColor(a.type),
                      }}
                      title={`${a.label}: ${a.count}`}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-4 pt-3 border-t border-sc-border">
            {legendActions.map((a) => (
              <div key={a.type} className="flex items-center gap-1.5">
                <span
                  className="w-2.5 h-2.5 rounded-sm shrink-0"
                  style={{ backgroundColor: getActionColor(a.type) }}
                />
                <span className="text-label text-sc-text-muted">{a.label}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </GlassCard>
  );
}

// ---- Recent Activity List ----

function RecentActivityList({
  events,
  isLoading,
}: {
  readonly events: AuditLogEntry[];
  readonly isLoading: boolean;
}) {
  if (isLoading) {
    return <LoadingSpinner resource="recent activity" />;
  }

  return (
    <GlassCard>
      <div className="flex items-center gap-2 mb-4">
        <Activity size={18} className="text-sc-text-muted" />
        <h3 className="text-h3 font-display text-sc-navy">Recent Activity</h3>
      </div>
      {events.length === 0 ? (
        <p className="text-body-sm text-sc-text-muted">
          No recent activity to display.
        </p>
      ) : (
        <div className="space-y-3">
          {events.map((event) => (
            <div
              key={event.id}
              className="flex items-start gap-3 pb-3 border-b border-sc-border/50 last:border-b-0 last:pb-0"
            >
              <span
                className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${SEVERITY_DOT[event.severity] ?? SEVERITY_DOT.INFO}`}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-label text-sc-text-muted whitespace-nowrap">
                    {relativeTime(event.timestamp)}
                  </span>
                </div>
                <p className="text-body-sm font-medium text-sc-text mt-0.5">
                  {formatEventType(event.eventType)}
                </p>
                <p className="text-label text-sc-text-muted">
                  {event.actorDisplayName ?? "System"}
                  {event.tenantName && event.tenantName.toUpperCase() !== "SYSTEM"
                    ? ` — ${event.tenantName}`
                    : ""}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </GlassCard>
  );
}

// ---- Format event type to friendly label ----

const EVENT_LABELS: Record<string, string> = {
  GDAP_RELATIONSHIP_CREATED: "Created GDAP Relationship",
  GDAP_RELATIONSHIP_TERMINATED: "Terminated GDAP Relationship",
  GDAP_ASSIGNMENTS_CREATED: "Created Access Assignments",
  PIM_SESSION_ACTIVATED: "Activated PIM Session",
  PIM_SESSION_EXTENDED: "Extended PIM Session",
  PIM_SESSION_DEACTIVATED: "Deactivated PIM Session",
  PIM_SESSION_REVOKED: "Revoked PIM Session",
  PIM_APPROVAL_REQUESTED: "Requested PIM Approval",
  PIM_APPROVAL_GRANTED: "Approved PIM Request",
  PIM_APPROVAL_DENIED: "Denied PIM Request",
  TECHNICIAN_ATTRIBUTES_UPDATED: "Updated Technician",
  TECHNICIAN_ACCESS_REVOKED: "Revoked Technician Access",
  TECHNICIAN_TIER_CHANGED: "Changed Technician Tier",
  CUSTOMER_CONFIG_CHANGED: "Updated Customer Config",
  CUSTOMER_ACCESS_TYPE_CHANGED: "Changed Access Strategy",
  CACHE_REFRESH_MANUAL: "Refreshed Cache",
};

function formatEventType(eventType: string): string {
  return EVENT_LABELS[eventType] ?? eventType.replaceAll("_", " ");
}

// ---- Technician Dashboard (Story 9) ----

function TechnicianDashboard() {
  const sessionsQuery = usePimSessionsMine();
  const activityQuery = useDashboardActivity(10, USER_ACTION_EVENTS);

  const sessions = sessionsQuery.data?.items ?? [];
  const activeSessions = sessions.filter((s) => s.status === "Active");
  const uniqueCustomers = new Set(
    sessions.map((s) => s.customerTenantId),
  ).size;
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const sessionsToday = sessions.filter(
    (s) => new Date(s.activatedAt) >= todayStart,
  ).length;

  const activity = activityQuery.data?.items ?? [];

  if (sessionsQuery.isError) {
    return <ErrorCard message="Unable to load dashboard data." />;
  }

  return (
    <div className="space-y-6">
      {/* Stat cards - 3 for technicians */}
      {sessionsQuery.isLoading ? (
        <LoadingSpinner resource="dashboard" />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard
            label="My Active Sessions"
            value={activeSessions.length}
            icon={ShieldCheck}
          />
          <StatCard
            label="My Customers"
            value={uniqueCustomers}
            icon={Users}
          />
          <StatCard
            label="Sessions Today"
            value={sessionsToday}
            icon={Activity}
          />
        </div>
      )}

      {/* Main content */}
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_370px] gap-6">
        <TechnicianSessionsTable sessions={sessions.slice(0, 10)} />

        <div className="space-y-6">
          {activityQuery.isLoading ? (
            <LoadingSpinner resource="activity" />
          ) : (
            <ActivityFeed events={activity} />
          )}
        </div>
      </div>
    </div>
  );
}
